import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, Award, MessageSquare, Image, Inbox, Mail, MapPin, TrendingUp, ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { adminGetStats, adminGetQuoteRequests } from '../api/api';

const FORMAT_DATE = { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [dernieres, setDernieres] = useState([]);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    Promise.all([adminGetStats(), adminGetQuoteRequests({ limit: 5 })])
      .then(([s, devis]) => {
        setStats(s);
        setDernieres(devis.items);
      })
      .catch((error) => console.error('Statistiques indisponibles', error))
      .finally(() => setChargement(false));
  }, []);

  if (chargement) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid sm:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!stats) {
    return <div className="p-8 text-gray-500">Statistiques indisponibles.</div>;
  }

  const aTraiter = stats.quotes_new + stats.messages_new;

  const contenus = [
    { icon: Package, label: 'Services', valeur: stats.services, lien: '/admin/services' },
    { icon: Award, label: 'Avantages', valeur: stats.benefits, lien: '/admin/benefits' },
    { icon: MessageSquare, label: 'Témoignages', valeur: stats.testimonials, lien: '/admin/testimonials' },
    { icon: Image, label: 'Réalisations', valeur: stats.projects, lien: '/admin/projects' },
    { icon: MapPin, label: 'Zones', valeur: stats.areas, lien: '/admin/areas' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="text-gray-600 mt-1">
          {aTraiter === 0
            ? 'Tout est à jour, rien n’attend de réponse.'
            : `${aTraiter} élément${aTraiter > 1 ? 's' : ''} en attente de votre réponse.`}
        </p>
      </div>

      {/* Ce qui demande une action passe en premier : c'est la raison
          pour laquelle on ouvre ce panel. */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Link
          to="/admin/quotes"
          className={`group rounded-xl border p-6 transition-colors ${
            stats.quotes_new > 0
              ? 'bg-blue-50 border-blue-200 hover:border-blue-400'
              : 'bg-white border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-600 text-sm font-medium">
                <Inbox className="w-4 h-4" aria-hidden="true" />
                Demandes de devis
              </div>
              <p className="mt-3 text-4xl font-bold text-gray-900">{stats.quotes_new}</p>
              <p className="text-sm text-gray-600 mt-1">
                {stats.quotes_new === 0 ? 'aucune en attente' : 'à traiter'}
                <span className="text-gray-400"> · {stats.quotes_total} au total</span>
              </p>
            </div>
            <ArrowRight
              className="w-5 h-5 text-gray-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all"
              aria-hidden="true"
            />
          </div>
        </Link>

        <Link
          to="/admin/messages"
          className={`group rounded-xl border p-6 transition-colors ${
            stats.messages_new > 0
              ? 'bg-amber-50 border-amber-200 hover:border-amber-400'
              : 'bg-white border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-600 text-sm font-medium">
                <Mail className="w-4 h-4" aria-hidden="true" />
                Messages de contact
              </div>
              <p className="mt-3 text-4xl font-bold text-gray-900">{stats.messages_new}</p>
              <p className="text-sm text-gray-600 mt-1">
                {stats.messages_new === 0 ? 'aucun non lu' : 'non lus'}
                <span className="text-gray-400"> · {stats.messages_total} au total</span>
              </p>
            </div>
            <ArrowRight
              className="w-5 h-5 text-gray-300 group-hover:text-amber-600 group-hover:translate-x-1 transition-all"
              aria-hidden="true"
            />
          </div>
        </Link>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-600" aria-hidden="true" />
            Activité
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700">
            <span className="text-2xl font-bold text-gray-900">{stats.quotes_last_7_days}</span>{' '}
            demande{stats.quotes_last_7_days > 1 ? 's' : ''} de devis ces sept derniers jours.
          </p>
        </CardContent>
      </Card>

      {dernieres.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Dernières demandes</CardTitle>
            <Link to="/admin/quotes" className="text-sm text-blue-600 hover:text-blue-700">
              Tout voir
            </Link>
          </CardHeader>
          <CardContent className="divide-y divide-gray-100">
            {dernieres.map((devis) => (
              <div key={devis.id} className="flex flex-wrap items-center gap-3 py-3 first:pt-0">
                <div className="flex-1 min-w-[180px]">
                  <p className="font-medium text-gray-900">{devis.name}</p>
                  <p className="text-sm text-gray-500">{devis.service}</p>
                </div>
                <span className="text-sm text-gray-400">
                  {new Date(devis.created_at).toLocaleDateString('fr-BE', FORMAT_DATE)}
                </span>
                {devis.status === 'new' && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                    nouveau
                  </span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Contenu du site
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {contenus.map(({ icon: Icone, label, valeur, lien }) => (
            <Link
              key={label}
              to={lien}
              className="bg-white border border-gray-200 hover:border-blue-600 rounded-xl p-4 transition-colors"
            >
              <Icone className="w-5 h-5 text-gray-400 mb-2" aria-hidden="true" />
              <p className="text-2xl font-bold text-gray-900">{valeur}</p>
              <p className="text-sm text-gray-600">{label}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
